import type { LensAggregate } from '~/types/leLens';

import { isNumeric } from '../lensChart';
import { ratioOfSumsFault } from '../ratioGuard';

export type MetricHeadline =
  | { kind: 'empty' }
  | { kind: 'refused'; reason: string }
  | { kind: 'value'; value: number; column: string; aggregate: LensAggregate; rowCount: number };

/**
 * A metric card with no configured view still has to show something
 * defensible, not the first numeric value of the first row read off as a
 * total. One row is already a total: nothing to aggregate. Many rows against
 * one numeric column can be summed, the same default a configured view would
 * use. Many rows against more than one numeric column has no single honest
 * reduction, so this refuses and says why rather than guessing a column.
 */
export function resolveMetricHeadline(rows: Array<Record<string, unknown>>, columns: Array<string>): MetricHeadline {
  if (!rows.length) {
    return { kind: 'empty' };
  }

  const numericColumns = columns.filter((column) => rows.some((row) => isNumeric(row[column])));
  if (!numericColumns.length) {
    return { kind: 'empty' };
  }

  if (rows.length === 1) {
    const column = numericColumns[0];
    return { kind: 'value', value: Number(rows[0][column]), column, aggregate: 'first', rowCount: 1 };
  }

  if (numericColumns.length > 1) {
    return {
      kind: 'refused',
      reason: `${rows.length} rows and ${numericColumns.length} numeric columns share this dataset, so there is no single number to show. Configure a view to say how they combine.`,
    };
  }

  const column = numericColumns[0];
  const fault = ratioOfSumsFault(column, 'sum', columns);
  if (fault) {
    return { kind: 'refused', reason: fault.message };
  }
  const value = rows.reduce((total, row) => total + (isNumeric(row[column]) ? Number(row[column]) : 0), 0);
  return { kind: 'value', value, column, aggregate: 'sum', rowCount: rows.length };
}
